import { immer } from "zustand/middleware/immer";

import { Chat, MemberDetailedInfo, Message, User } from "@/types";
import { createStore } from "@/utils";

export type ShortUserChatInfo = Pick<User, "id"> & Pick<MemberDetailedInfo, "profileImage" | "firstName" | "visit">;

export type ChatState = {
  editMode: Message | null;
  chatsType: "all" | "archived";
  mobileView: "list" | "chat";
  currentChat?: Chat;
  mutedChatIds: Chat["id"][];
  readMessagesIdsForRequest: string[];
  translatedMessages: { chatId: Chat["id"]; messageId: Message["id"]; originalTranslation: string }[];
};

type ChatActions = {
  actions: {
    setCurrentChat: (chat?: Chat) => void;
    changeEditMode: (mode: ChatState["editMode"]) => void;
    changeMobileView: (view: ChatState["mobileView"]) => void;
    changeChatsType: (view: ChatState["chatsType"]) => void;
    mutateChatIds: (data: { id?: Chat["id"]; chatIds?: Chat["id"][] }) => void;
    mutateReadMessagesIdsForRequestIds: (ids: string[]) => void;
    addTranslatedMessage: (message: {
      chatId: Chat["id"];
      messageId: Message["id"];
      originalTranslation: string;
    }) => void;
    removeTranslatedMessage: (message: { chatId: Chat["id"]; messageId: Message["id"] }) => void;
  };
};

const defaultChatValues: ChatState = {
  chatsType: "all",
  mobileView: "list",
  editMode: null,
  mutedChatIds: [],
  readMessagesIdsForRequest: [],
  translatedMessages: [],
};

const useChatStore = createStore<ChatState & ChatActions>(
  immer((set) => ({
    ...defaultChatValues,
    actions: {
      changeMobileView: (view) =>
        set((state) => {
          state.mobileView = view;
        }),
      setCurrentChat: (chat) =>
        set((state) => {
          state.currentChat = chat;
        }),
      changeChatsType: (type) => {
        set((state) => {
          state.chatsType = type;
        });
      },
      mutateChatIds: ({ id, chatIds }) => {
        set((state) => {
          if (id) {
            const chatIdIndex = state.mutedChatIds.findIndex((chatId) => chatId === id);

            if (chatIdIndex === -1) {
              state.mutedChatIds.push(id);
            } else {
              state.mutedChatIds.splice(chatIdIndex, 1);
            }
          }

          if (chatIds) {
            state.mutedChatIds = chatIds;
          }
        });
      },
      mutateReadMessagesIdsForRequestIds: (ids) => {
        set((state) => {
          state.readMessagesIdsForRequest = ids;
        });
      },
      changeEditMode: (mode) =>
        set((state) => {
          state.editMode = mode;
        }),
      addTranslatedMessage: (message) =>
        set((state) => {
          state.translatedMessages.push(message);
        }),
      removeTranslatedMessage: (message) =>
        set((state) => {
          const messageIndex = state.translatedMessages.findIndex(
            ({ chatId, messageId }) => chatId === message.chatId && messageId === message.messageId
          );

          if (messageIndex > -1) {
            state.translatedMessages.splice(messageIndex, 1);
          }
        }),
    },
  }))
);

export { useChatStore };
